import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Brain, 
  Trophy, 
  ShoppingCart, 
  BarChart3, 
  Activity, 
  Zap, 
  Home, 
  ArrowLeft,
  Users,
  Crown,
  TrendingUp,
  Target
} from 'lucide-react';

const FeaturesNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const features = [
    {
      path: '/features/ai-predictions',
      name: 'IA Predictions',
      icon: Brain,
      color: 'from-purple-600 to-blue-600',
      description: 'Análisis inteligente de partidos'
    },
    {
      path: '/features/tournaments',
      name: 'Torneos',
      icon: Trophy,
      color: 'from-yellow-600 to-orange-600',
      description: 'Compite en torneos épicos'
    },
    {
      path: '/features/marketplace',
      name: 'Marketplace',
      icon: ShoppingCart,
      color: 'from-green-600 to-blue-600',
      description: 'Compra y vende predicciones'
    },
    {
      path: '/features/analytics',
      name: 'Analytics',
      icon: BarChart3,
      color: 'from-blue-600 to-purple-600',
      description: 'Estadísticas avanzadas'
    },
    {
      path: '/features/live-streaming',
      name: 'Live Streaming',
      icon: Activity,
      color: 'from-red-600 to-pink-600',
      description: 'Partidos en vivo'
    },
    {
      path: '/features/gamification',
      name: 'Gamificación',
      icon: Zap,
      color: 'from-orange-600 to-red-600',
      description: 'Sistema de niveles y logros'
    }
  ];

  const currentFeature = features.find(f => f.path === location.pathname);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-xl border-b border-white/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/gta-app/home')}
                className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Volver</span>
              </button>
              
              {currentFeature && (
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 bg-gradient-to-r ${currentFeature.color} rounded-lg flex items-center justify-center`}>
                    <currentFeature.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-white">{currentFeature.name}</h1>
                    <p className="text-sm text-gray-300">{currentFeature.description}</p>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Navegación lateral */}
      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed lg:static inset-y-0 left-0 z-30 w-64 bg-white/10 backdrop-blur-xl border-r border-white/20 transform ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-300 ease-in-out`}>
          <div className="p-6">
            <div className="mb-8">
              <h2 className="text-lg font-bold text-white mb-2">Funcionalidades</h2>
              <p className="text-sm text-gray-300">Explora todas las nuevas características</p>
            </div>

            <nav className="space-y-2">
              {features.map((feature) => {
                const Icon = feature.icon;
                const isActive = location.pathname === feature.path;
                
                return (
                  <button
                    key={feature.path}
                    onClick={() => {
                      navigate(feature.path);
                      setIsMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive 
                        ? `bg-gradient-to-r ${feature.color} text-white shadow-lg` 
                        : 'text-gray-300 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <div className="text-left">
                      <div className="font-medium">{feature.name}</div>
                      <div className="text-xs opacity-75">{feature.description}</div>
                    </div>
                  </button>
                );
              })}
            </nav>

            {/* Stats rápidas */}
            <div className="mt-8 p-4 bg-white/5 rounded-xl border border-white/10">
              <h3 className="text-sm font-semibold text-white mb-3">Stats Rápidas</h3>
              <div className="space-y-2 text-xs text-gray-300">
                <div className="flex justify-between">
                  <span>Apuestas Totales:</span>
                  <span className="text-green-400">1,247</span>
                </div>
                <div className="flex justify-between">
                  <span>Win Rate:</span>
                  <span className="text-blue-400">78.5%</span>
                </div>
                <div className="flex justify-between">
                  <span>Ganancias:</span>
                  <span className="text-yellow-400">$92,700</span>
                </div>
                <div className="flex justify-between">
                  <span>Nivel:</span>
                  <span className="text-purple-400">Pro</span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Overlay para móvil */}
        {isMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-20 lg:hidden"
            onClick={() => setIsMenuOpen(false)}
          />
        )}

        {/* Contenido principal */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <button 
                onClick={() => navigate('/gta-app/home')}
                className="hover:text-white transition-colors"
              >
                Home
              </button>
              <span>/</span>
              <span className="text-white">Features</span>
              {currentFeature && (
                <>
                  <span>/</span>
                  <span className="text-white">{currentFeature.name}</span>
                </>
              )}
            </div>

            {/* Grid de acceso rápido */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {features.map((feature) => {
                const Icon = feature.icon;
                const isActive = location.pathname === feature.path;
                
                return (
                  <div
                    key={feature.path}
                    onClick={() => navigate(feature.path)}
                    className={`relative cursor-pointer group ${
                      isActive ? 'ring-2 ring-white/50' : ''
                    }`}
                  >
                    <div className={`bg-gradient-to-r ${feature.color} rounded-xl p-6 h-32 flex flex-col justify-between transition-all group-hover:scale-105 group-hover:shadow-xl`}>
                      <div className="flex items-center justify-between">
                        <Icon className="w-8 h-8 text-white" />
                        {isActive && (
                          <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
                        )}
                      </div>
                      
                      <div>
                        <h3 className="text-white font-bold text-lg">{feature.name}</h3>
                        <p className="text-white/80 text-sm">{feature.description}</p>
                      </div>
                    </div>
                    
                    {isActive && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center">
                        <div className="w-3 h-3 bg-green-500 rounded-full" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Información adicional */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <h2 className="text-xl font-bold text-white mb-4">¿Qué hay de nuevo?</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">IA Predictions</h3>
                    <p className="text-sm text-gray-300">
                      Análisis inteligente que combina estadísticas, formaciones y tendencias para predicciones más precisas.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Torneos</h3>
                    <p className="text-sm text-gray-300">
                      Compite contra otros usuarios en torneos con premios reales y rankings globales.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ShoppingCart className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Marketplace</h3>
                    <p className="text-sm text-gray-300">
                      Compra predicciones de expertos o vende las tuyas propias para ganar dinero extra.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default FeaturesNavigation; 